import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import type { IndexDef, LevelCode, ObjectiveEvent } from '@/api/types/readiness'
import { levelNum } from '@/api/types/readiness'
import { levelName } from '@/lib/indexes/registry'
import { stepSparklineGeometry, type SparkStep } from '@/lib/readiness/miniChart'
import { scopeGrowthFor } from '@/lib/readiness/progress'
import { TipBody } from '@/components/readiness/Term'
import { formatDate } from '@/components/common/format'

// ── Step sparkline (I6 §7, §8; Note 3) ──────────────────────────────────────
// One component for the merged table, the Objectives moved quick view and the
// Progress Report. It takes a pixel width and height and draws at that size,
// so the circles and ◆ are true shapes at any width (never
// `preserveAspectRatio="none"`). Shared visual language: L0–L3 guides, the
// domain-coloured step line, teal dashed drop and ◆ for coverage-driven drops,
// a filled dot at today's level, a dashed stub to a hollow dot at a pending
// suggested level. The stub always runs from 14px left of today, so its angle
// is the same on every row.
//
// Hover (§8, reverses the I5 deferral): each flat segment, step, ◆ and the
// stub has an invisible hit area at least 10px wide; after ~300ms an
// informational tooltip names it, the target brightens and the rest of the
// line dims to 60%. `interactive={false}` (print) draws no hit areas.

export type SparkHoverTarget = 'segment' | 'step' | 'drop' | 'stub'

const DAY = 86_400_000
const DELAY = 300
const TIP_W = 300

const fmtRange = (a: number, b: number) => {
  const da = new Date(a)
  const db = new Date(b)
  const start = da.getFullYear() === db.getFullYear() ? da.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : formatDate(da.toISOString())
  return `${start} – ${formatDate(db.toISOString())}`
}
const actorOf = (e: ObjectiveEvent) => {
  const who = e.actor === 'system' ? 'System' : e.actor
  return e.event_type === 'confirmed' ? `confirmed by ${who}` : e.event_type === 'overridden' ? `overridden by ${who}` : `declared by ${who}`
}

// The ◆ tooltip (I5 §10.2), unchanged by I6.
export function CoverageDropTip({ events, code, event }: { events: ObjectiveEvent[]; code: string; event: ObjectiveEvent }) {
  const growth = scopeGrowthFor(events, event)
  const se = [...events].filter((x) => x.event_type === 'scope_expanded' && x.objective_code === code && x.occurred_at <= event.occurred_at).pop()
  const back = events.find((x) => x.objective_code === code && x.occurred_at > event.occurred_at && (x.event_type === 'confirmed' || x.event_type === 'declared' || x.event_type === 'overridden') && x.to_level === event.from_level)
  return (
    <TipBody title={`Coverage-driven drop · ${formatDate(event.occurred_at)}`}>
      <p>{event.from_level} → {event.to_level} · Discovery found {growth != null ? growth.toLocaleString() : 'more'} more assets{se?.assets_known_from != null && se.assets_known_to != null ? ` (${se.assets_known_from.toLocaleString()} → ${se.assets_known_to.toLocaleString()})` : ''}. The level now covers a larger estate. This is better visibility, not a regression, and it doesn’t count against pace.{back ? ` Back to ${event.from_level} on ${formatDate(back.occurred_at)}.` : ''}</p>
    </TipBody>
  )
}

type Target = { key: string; kind: SparkHoverTarget; cx: number; top: number; content: ReactNode }

export function StepSparkline({ index, events, code, from, to, color, width = 200, height = 48, suggested = null, ariaLabel, interactive = true, onHover, testId }: { index: IndexDef; events: ObjectiveEvent[]; code: string; from: number; to: number; color: string; width?: number; height?: number; suggested?: LevelCode | null; ariaLabel?: string; interactive?: boolean; onHover?: (target: SparkHoverTarget) => void; testId?: string }) {
  const g = stepSparklineGeometry(events, code, from, to, width, height)
  const todayX = width - g.padX
  const stub = suggested && suggested !== g.level ? { x1: todayX - 14, y1: g.y(g.level), x2: todayX, y2: g.y(suggested) } : null
  const [hover, setHover] = useState<Target | null>(null)
  const pending = useRef<string | null>(null)
  const timer = useRef<number | null>(null)
  const clear = () => { if (timer.current) window.clearTimeout(timer.current); timer.current = null; pending.current = null }
  const hide = () => { clear(); setHover(null) }
  useEffect(() => {
    if (!hover) return
    const onScroll = () => setHover(null)
    window.addEventListener('scroll', onScroll, true)
    return () => window.removeEventListener('scroll', onScroll, true)
  }, [hover])
  useEffect(() => clear, [])
  const dim = (key: string) => (hover && hover.key !== key ? 0.6 : 1)
  const lvl = (l: LevelCode) => `L${levelNum(l)} ${levelName(index, l)}`

  const segTip = (s: (typeof g.segments)[number]) => {
    const days = Math.max(0, Math.round((s.to - s.from) / DAY))
    return s.current ? `${lvl(s.level)} · since ${formatDate(new Date(s.from).toISOString())} · ${days} d · current` : `${lvl(s.level)} · ${fmtRange(s.from, s.to)} · ${days} d`
  }
  const stepTip = (st: SparkStep) => `${st.from} → ${st.to} · ${formatDate(st.event.occurred_at)} · ${actorOf(st.event)}`

  // The nearest target to the pointer, with a tolerance that makes every hit
  // area at least 10px wide (§8.1): a one-day segment is still hoverable, and a
  // riser beside it still wins when the pointer is on the riser.
  const pick = (x: number, y: number, r: DOMRect): Target | null => {
    let best: Target | null = null
    let bestD = Infinity
    const segTarget = (s: (typeof g.segments)[number], i: number): Target => ({ key: `s${i}`, kind: 'segment', cx: r.left + Math.min(Math.max(x, s.x1), s.x2), top: r.top + s.y - 7, content: segTip(s) })
    // Directly over a segment (within its own span, close to its line): the
    // segment wins outright, so a segment a few pixels wide beside a riser is
    // still hoverable.
    g.segments.forEach((s, i) => {
      if (x < s.x1 || x > s.x2) return
      const d = Math.abs(y - s.y)
      if (d <= 4 && d < bestD) { bestD = d; best = segTarget(s, i) }
    })
    if (best) return best
    g.segments.forEach((s, i) => {
      if (x < s.x1 - 5 || x > s.x2 + 5) return
      const d = Math.abs(y - s.y)
      if (d <= 7 && d < bestD) { bestD = d; best = segTarget(s, i) }
    })
    g.steps.forEach((st, i) => {
      const lo = Math.min(st.y1, st.y2) - 5
      const hi = Math.max(st.y1, st.y2) + 5
      if (y < lo || y > hi) return
      const d = Math.abs(x - st.x)
      // Bias toward the riser: on it, the step wins over the segments that meet it.
      if (d <= 5 && d - 3 < bestD) { bestD = d - 3; best = { key: `t${i}`, kind: st.drop ? 'drop' : 'step', cx: r.left + st.x, top: r.top + lo, content: st.drop ? <CoverageDropTip events={events} code={code} event={st.event} /> : stepTip(st) } }
    })
    if (stub) {
      const lo = Math.min(stub.y1, stub.y2) - 5
      const hi = Math.max(stub.y1, stub.y2) + 5
      if (x >= stub.x1 - 5 && x <= stub.x2 + 5 && y >= lo && y <= hi) {
        // Distance to the stub line.
        const dx = stub.x2 - stub.x1
        const dy = stub.y2 - stub.y1
        const t = Math.max(0, Math.min(1, ((x - stub.x1) * dx + (y - stub.y1) * dy) / (dx * dx + dy * dy || 1)))
        const d = Math.hypot(x - (stub.x1 + t * dx), y - (stub.y1 + t * dy))
        if (d <= 6 && d - 4 < bestD) { bestD = d - 4; best = { key: 'stub', kind: 'stub', cx: r.left + (stub.x1 + stub.x2) / 2, top: r.top + lo, content: `Sync suggests ${suggested} · awaiting confirmation` } }
      }
    }
    return best
  }
  const onMove = (e: ReactMouseEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    const t = pick(e.clientX - r.left, e.clientY - r.top, r)
    if (!t) { if (hover || pending.current) hide(); return }
    if (hover?.key === t.key || pending.current === t.key) return
    clear()
    pending.current = t.key
    timer.current = window.setTimeout(() => {
      pending.current = null
      setHover(t)
      onHover?.(t.kind)
    }, DELAY)
  }

  return (
    <>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel ?? `${code} confirmed level over time, now ${g.level}`} className="block shrink-0" data-testid={testId} onMouseMove={interactive ? onMove : undefined} onMouseLeave={interactive ? hide : undefined}>
        {(['L0', 'L1', 'L2', 'L3'] as LevelCode[]).map((l) => (
          <line key={l} x1={g.padX} x2={width - g.padX} y1={g.y(l)} y2={g.y(l)} stroke="var(--rd-border-control)" strokeWidth={1} strokeDasharray="2 3" opacity={0.8} />
        ))}
        {g.segments.map((s, i) => s.x2 - s.x1 > 0.01 ? <line key={`s${i}`} x1={s.x1} x2={s.x2} y1={s.y} y2={s.y} stroke={color} strokeWidth={1.75} strokeLinecap="butt" opacity={dim(`s${i}`)} data-testid="spark-segment" /> : null)}
        {g.steps.map((st, i) => st.drop ? (
          <g key={`t${i}`} opacity={dim(`t${i}`)} data-testid="mini-drop">
            <line x1={st.x} x2={st.x} y1={st.y1} y2={st.y2} stroke="var(--rd-coverage)" strokeWidth={1.5} strokeDasharray="2 2" data-testid="spark-drop" />
            <rect x={st.x - 4} y={st.y1 - 4} width={8} height={8} transform={`rotate(45 ${st.x} ${st.y1})`} fill="var(--rd-coverage)" />
          </g>
        ) : (
          <line key={`t${i}`} x1={st.x} x2={st.x} y1={st.y1} y2={st.y2} stroke={color} strokeWidth={1.75} opacity={dim(`t${i}`)} data-testid="spark-step" />
        ))}
        {stub && (
          <g data-testid="mini-suggested" opacity={dim('stub')}>
            <line x1={stub.x1} x2={stub.x2} y1={stub.y1} y2={stub.y2} stroke={color} strokeWidth={1.5} strokeDasharray="2 2" data-testid="spark-stub" />
            <circle cx={stub.x2} cy={stub.y2} r={3.5} fill="var(--rd-bg-card)" stroke={color} strokeWidth={1.5} />
          </g>
        )}
        <circle cx={todayX} cy={g.y(g.level)} r={3.5} fill={color} data-testid="mini-today" />
        {/* One transparent plane receives the pointer; targets are resolved by distance (§8.1). */}
        {interactive && <rect className="rd-spark-hit" x={0} y={0} width={width} height={height} data-testid="spark-hit" />}
      </svg>
      {hover && createPortal(
        <div role="tooltip" className="rd rd-tooltip" data-testid="spark-tooltip" style={{ top: hover.top - 8, left: Math.min(Math.max(8, hover.cx - TIP_W / 2), window.innerWidth - TIP_W - 8), width: TIP_W, maxWidth: TIP_W, transform: 'translateY(-100%)' }}>
          {typeof hover.content === 'string' ? <p>{hover.content}</p> : hover.content}
        </div>,
        document.body
      )}
    </>
  )
}
